import { MoneyMapVisual } from '@fruitful/money-map';
import { AnalyticsUserEvent } from '@fruitful/types';
import { router } from 'expo-router';
import { useAtomValue } from 'jotai';
import React, { useState } from 'react';
import { ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    ArrowLeft,
    BodyLargeRegular,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CircleCheck,
    Icon,
    IconButton,
    isNonMobileWeb,
    isWeb,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { track } from '../../logger/Logger';
import { confirmedReviewResultsAtom } from '../state/OnboardingAtoms';

const OnboardingConfirmMoneyMap = () => {
    const { top: safeTop, bottom: safeBottom } = useSafeAreaInsets();
    const paddingTop = isWeb ? 24 : safeTop + 24;

    const results = useAtomValue(confirmedReviewResultsAtom);

    const income = results?.moneyMap?.incomeTotalCents ?? 0;
    const bills = results?.moneyMap?.billsTotalCents ?? 0;
    const spend = results?.moneyMap?.spendTotalCents ?? 0;
    const goals = results?.moneyMap?.goalsTotalCents ?? 0;

    const VISUAL_NATURAL_HEIGHT = 520;
    const [middleSectionHeight, setMiddleSectionHeight] = useState(0);
    const [boxWidth, setBoxWidth] = useState(0);
    const boxPadding = 32;
    const availableForVisual = middleSectionHeight - boxPadding;
    const visualScale =
        !isNonMobileWeb && availableForVisual > 0
            ? Math.min(1, availableForVisual / VISUAL_NATURAL_HEIGHT)
            : 1;

    if (results == null) {
        router.replace('/');
        return;
    }

    const headerContent = (
        <>
            <XStack
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                position="relative"
            >
                <IconButton
                    variant={ButtonVariant.SECONDARY}
                    icon={ArrowLeft}
                    onPress={router.back}
                />
                <BodyLargeRegular
                    position="absolute"
                    left={0}
                    right={0}
                    textAlign="center"
                    pointerEvents="none"
                >
                    Nice job!
                </BodyLargeRegular>
                <View width={40} />
            </XStack>
            <BodyMediumRegular color="$gray1000" textAlign="center">
                Your first Money Map is ready to go. Next, you&apos;ll open
                accounts and turn on your system.
            </BodyMediumRegular>
        </>
    );

    const visualContent = (
        <View
            width="100%"
            padding={16}
            borderRadius={24}
            backgroundColor="$leafLightest"
            position="relative"
            overflow="hidden"
            onLayout={(e) => {
                setBoxWidth(e.nativeEvent.layout.width);
            }}
        >
            <Icon
                icon={CircleCheck}
                color="$leaf"
                size={20}
                position="absolute"
                top={16}
                right={16}
                zIndex={1}
            />
            {isNonMobileWeb ? (
                <MoneyMapVisual
                    mode="go-flow"
                    incomeTotalCents={income}
                    billsTotalCents={bills}
                    spendTotalCents={spend}
                    goalsTotalCents={goals}
                    maxWidth={500}
                    animated={false}
                />
            ) : (
                <View
                    style={{
                        transform: [{ scale: visualScale }],
                        transformOrigin: 'top center',
                        width:
                            visualScale < 1 && boxWidth > 0
                                ? (boxWidth - boxPadding) / visualScale
                                : '100%',
                        alignSelf: 'center',
                        height: VISUAL_NATURAL_HEIGHT * visualScale,
                    }}
                >
                    <MoneyMapVisual
                        mode="go-flow"
                        incomeTotalCents={income}
                        billsTotalCents={bills}
                        spendTotalCents={spend}
                        goalsTotalCents={goals}
                        maxWidth={500}
                        animated={false}
                    />
                </View>
            )}
        </View>
    );

    const buttonContent = (
        <YStack gap={16} width="100%" alignItems="center">
            <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                text="Next"
                width="full"
                onPress={() => {
                    // The Money Map was already saved on the previous screen's
                    // "Looks good" button, so this is just a forward step.
                    track(AnalyticsUserEvent.ONBOARDING_GOYO_CONFIRMED);
                    router.navigate({
                        pathname: '/roadmap/go/start',
                        params: { from: 'onboarding' },
                    });
                }}
            />
        </YStack>
    );

    return (
        <YStack width="100%" flex={1} maxWidth={500} alignSelf="center">
            {isNonMobileWeb ? (
                <ScrollView
                    style={{ flex: 1 }}
                    contentContainerStyle={{
                        alignItems: 'center',
                        paddingTop,
                        paddingHorizontal: 24,
                        paddingBottom: 24,
                        gap: 12,
                    }}
                    showsVerticalScrollIndicator
                >
                    {headerContent}
                    {visualContent}
                    {buttonContent}
                </ScrollView>
            ) : (
                <YStack
                    flex={1}
                    alignItems="center"
                    paddingTop={paddingTop}
                    paddingHorizontal={24}
                    paddingBottom={safeBottom || 12}
                    gap={12}
                >
                    {headerContent}
                    <View
                        flex={1}
                        width="100%"
                        alignItems="center"
                        onLayout={(e) => {
                            setMiddleSectionHeight(e.nativeEvent.layout.height);
                        }}
                    >
                        {visualContent}
                    </View>
                    {buttonContent}
                </YStack>
            )}
        </YStack>
    );
};

export default OnboardingConfirmMoneyMap;
